import { Form } from 'antd';
import { UsersComponent } from 'components/UsersComponent';
import { AddMemberForm } from 'components/UsersComponent/add-member';
import { History } from 'components/UsersComponent/history';
import { Rights } from 'components/UsersComponent/rights';
import { MessageStates } from 'containers/MessageBox';
import * as React from 'react';

import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import routes from 'routes';
import {
  AccountType,
  PermissionType,
  SubscriptionPlan,
  TablePermission,
} from 'store/api/enums';
import { turnDrawerOn, turnDrawerOff, turnMessageOn } from 'store/app/actions';
import {
  DrawerType,
  CurrentModal,
  LicenseType,
  AdvancedUserRightsCodes,
} from 'store/app/enums';
import { getDrawerOpen } from 'store/app/selectors';
import { ACCESS_RIGHTS_DRAWER_WIDTH } from 'store/app/constants';
import { DrawerData, MessageData } from 'store/app/types';
import { getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import { resendActivationRequest } from 'store/signup/actions';
import {
  deleteUserRequest,
  addMemberRequest,
  fetchUsersRequest,
  setUserPermissionsRequest,
  fetchUserHistoryRequest,
  fetchUserRightsRequest,
  setUserRightsRequest,
  setAllUserRightsRequest,
  fetchAdvancedUserRightsRequest,
  assignAdvancedUserRightRequest,
  assignAdvancedUserRightToAllRequest,
} from 'store/user/actions';
import {
  getUserHistory,
  getUserHistoryLoading,
  getUserLoading,
  getUserMembers,
  getUserRights,
  getUserRightsLoading,
  selectUserAdvancedUserRights,
} from 'store/user/selectors';
import { getOrgMappedOrganisations } from 'store/organisation/selectors';
import { UserRights } from 'store/user/models';
import { fetchProfilePictureRequest } from 'store/account/actions';
import { checkPageLoaded } from 'utils/check-page-loaded';
import {
  hasUnlimitedLicense,
  isSmallOrMediumPlanLocked,
} from 'utils/subscriptionPlanGate';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Preference,
  PreferenceActions,
  usePreferences,
  PreferenceSections,
} from 'utils/preferences';
import { User } from 'store/auth/models';
import { getCurrentSubscription } from 'store/billing/selectors';

export const PAGE_SIZE_USERS = 'page-size-users';
/** Persisted list vs card layout for the users page (`list` | `card`). */
export const USERS_VIEW_LAYOUT = 'users-view-layout';

export interface NewMember {
  firstName: string;
  lastName: string;
  email: string;
  isClient: boolean;
}

export const UsersPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector(getUserLoading);
  const rightsLoading = useSelector(getUserRightsLoading);
  const historyLoading = useSelector(getUserHistoryLoading);
  const membersRaw = useSelector(getUserMembers);
  const currentUser = useSelector(getAuthUser);
  const history = useSelector(getUserHistory);
  const rights = useSelector(getUserRights);
  const advancedUserRights = useSelector(selectUserAdvancedUserRights);
  const drawerOpen = useSelector(getDrawerOpen);
  const mappedOrganisations = useSelector(getOrgMappedOrganisations);
  const currentSubscription = useSelector(getCurrentSubscription);
  const userLicence = useSelector(getAuthUserLicence);
  const location = useLocation();
  const [form] = Form.useForm();
  const [currentModal, setCurrentModal] = React.useState<CurrentModal>(
    CurrentModal.NONE
  );
  const [members, setMembers] = React.useState<User[]>([]);
  const [rightsTargetAccountUuid, setRightsTargetAccountUuid] = React.useState<
    string | null
  >(null);
  const wasModalOpenRef = React.useRef(false);
  const { t } = useTranslation();

  React.useEffect(() => {
    if (membersRaw) {
      if (isSmallOrMediumPlanLocked(currentSubscription, userLicence)) {
        setMembers(
          membersRaw.filter(
            (m) => AccountType[m.accountType] !== AccountType.CLIENT
          )
        );
      } else {
        setMembers(membersRaw);
      }
    }
  }, [membersRaw, currentSubscription, userLicence]);

  const preferences = usePreferences();
  const handlePreferences = (
    action: PreferenceActions,
    preference: Preference
  ) => {
    if (currentUser)
      return preferences(
        action,
        PreferenceSections.USERS,
        preference,
        currentUser?.userUuid
      );
    return false;
  };

  const goBack = () => {
    navigate(routes.main);
  };

  const openMemberWindow = () => {
    const modalData: DrawerData = {
      type: DrawerType.ADD_MEMBER,
      title: t('users.add-a-user'),
      component: <AddMemberForm saveNewMember={saveNewMember} />,
    };

    dispatch(turnDrawerOn(modalData));
  };

  const openHistoryWindow = (userId: string) => {
    dispatch(fetchUserHistoryRequest(userId));
    setCurrentModal(CurrentModal.HISTORY);
  };

  const closeCurrentDrawer = React.useCallback(() => {
    setCurrentModal(CurrentModal.NONE);
    dispatch(turnDrawerOff());
  }, [dispatch]);

  const openRightsWindow = (accountId: string) => {
    setRightsTargetAccountUuid(accountId);
    const member = members?.find((m) => m.accountUuid === accountId);
    dispatch(fetchUserRightsRequest(accountId, member?.userUuid));
    setCurrentModal(CurrentModal.RIGHTS);
  };

  React.useEffect(() => {
    if (currentModal !== CurrentModal.HISTORY) return;

    dispatch(
      turnDrawerOn({
        type: DrawerType.HISTORY,
        title: `${t('users.login-history')}`,
        component: (
          <History
            history={history}
            loading={historyLoading}
            onClose={closeCurrentDrawer}
          />
        ),
      })
    );
  }, [currentModal, history, historyLoading, closeCurrentDrawer, dispatch, t]);

  React.useEffect(() => {
    if (currentModal !== CurrentModal.RIGHTS || !rightsTargetAccountUuid) return;

    const rightsAccountUuid = rights?.[0]?.accountUuid;
    if (
      rightsAccountUuid &&
      rightsAccountUuid !== rightsTargetAccountUuid
    ) {
      return;
    }

    const selectedMember = members.find(
      (m) => m.accountUuid === rightsTargetAccountUuid
    );
    const filteredRights =
      selectedMember?.accountType &&
      AccountType[selectedMember.accountType] === AccountType.CLIENT
        ? advancedUserRights?.filter(
            (aur) =>
              aur.operationRightCode ===
              AdvancedUserRightsCodes.VAT_NUMBER_CHECKER
          )
        : advancedUserRights;
    const isAdvancedUserRights =
      currentSubscription?.description === SubscriptionPlan.LARGE_PLAN ||
      currentSubscription?.description === SubscriptionPlan.ENTERPRISE_PLAN ||
      hasUnlimitedLicense(userLicence) ||
      userLicence?.licenseType === LicenseType.TRIAL;

    dispatch(
      turnDrawerOn({
        type: DrawerType.RIGHTS,
        title: `${t('users.access-rights')}`,
        width: ACCESS_RIGHTS_DRAWER_WIDTH,
        contentKey: rightsTargetAccountUuid,
        component: (
          <Rights
            loading={rightsLoading}
            rights={rights}
            saveUserRights={saveUserRights}
            saveAllUserRights={saveAllUserRights}
            entities={mappedOrganisations}
            onClose={closeCurrentDrawer}
            isAdvancedUserRights={isAdvancedUserRights}
            advancedUserRights={filteredRights}
            setAdvancedUserRight={setAdvancedUserRight}
            setAdvancedUserRightToAll={setAdvancedUserRightToAll}
          />
        ),
      })
    );
  }, [
    currentModal,
    rights,
    rightsLoading,
    members,
    rightsTargetAccountUuid,
    mappedOrganisations,
    currentSubscription,
    userLicence,
    advancedUserRights,
    closeCurrentDrawer,
    dispatch,
    t,
  ]);

  React.useEffect(() => {
    // Reset stale modal mode only when drawer actually transitions from open -> closed.
    if (wasModalOpenRef.current && !drawerOpen) {
      setCurrentModal(CurrentModal.NONE);
    }
    wasModalOpenRef.current = drawerOpen;
  }, [drawerOpen]);

  React.useEffect(() => {
    drawerOpen && form.resetFields();
  }, [drawerOpen]);

  const saveNewMember = (newMember: NewMember) => {
    dispatch(addMemberRequest(newMember));
    dispatch(turnDrawerOff());
  };

  const deleteUser = (userToDeleteId: string) => {
    currentUser &&
      userToDeleteId &&
      dispatch(deleteUserRequest(userToDeleteId));
  };

  const resendActivation = (email: string) => {
    if (email) {
      const message: MessageData = {
        title: t('common.sending-request'),
        description: t('common.be-patient'),
        type: MessageStates.INFO,
        duration: 2,
      };
      dispatch(turnMessageOn(message));
      dispatch(resendActivationRequest(email, true));
    }
  };

  React.useEffect(() => {
    /*  if (!members && !loading) {
      dispatch(fetchUsersRequest());
    }

    if (!advancedUserRights) {
      dispatch(fetchAdvancedUserRightsRequest());
    } */

    currentUser && checkPageLoaded(location.pathname, routes.users);
  }, []);

  const saveUserPermissions = (
    accountUuid: string,
    permissionType: PermissionType,
    enabled: boolean
  ) => {
    dispatch(setUserPermissionsRequest(accountUuid, permissionType, enabled));
  };

  const saveUserRights = (
    userRights: UserRights,
    tablePermission: TablePermission
  ) => {
    const accountUuid = userRights.accountUuid;
    const userToModify = members?.find((m) => m.accountUuid === accountUuid);
    const userToSend = userToModify ? userToModify : undefined;

    dispatch(setUserRightsRequest(userRights, tablePermission, userToSend));
  };

  const saveAllUserRights = (
    userRights: UserRights[],
    tablePermission: TablePermission
  ) => {
    const accountUuid = userRights[0].accountUuid;
    const userToModify = members?.find((m) => m.accountUuid === accountUuid);
    const userToSend = userToModify ? userToModify : undefined;

    dispatch(setAllUserRightsRequest(userRights, tablePermission, userToSend));
  };

  const setAdvancedUserRight = (
    accountUuid: string,
    organisationId: string,
    operationIds: string[]
  ) => {
    if (accountUuid) {
      dispatch(
        assignAdvancedUserRightRequest(
          accountUuid,
          organisationId,
          operationIds
        )
      );
    } else {
      dispatch(
        turnMessageOn({
          title: t('users.error-saving-rights'),
          description: t('users.error-saving-rights-description'),
          duration: 2,
          type: MessageStates.ERROR,
        })
      );
    }
  };

  const setAdvancedUserRightToAll = (
    accountUuid: string,
    organisationIds: string[],
    operationIds: string[]
  ) => {
    if (accountUuid) {
      dispatch(
        assignAdvancedUserRightToAllRequest(
          accountUuid,
          organisationIds,
          operationIds
        )
      );
    } else {
      dispatch(
        turnMessageOn({
          title: t('users.error-saving-rights'),
          description: t('users.error-saving-rights-description'),
          duration: 2,
          type: MessageStates.ERROR,
        })
      );
    }
  };

  const getPicture = (userUuid: string) =>
    dispatch(fetchProfilePictureRequest(userUuid));
  return (
    <React.Fragment>
      <Helmet>
        <title>{`${t('main.users')} | ${process.env.PROJECT_NAME}`}</title>
        <meta name="description" content={t('users.page-meta-description')} />
      </Helmet>
      {members && (
        <UsersComponent
          loading={loading}
          members={members}
          currentUser={currentUser}
          currentSubscription={currentSubscription}
          saveUserPermissions={saveUserPermissions}
          openMemberWindow={openMemberWindow}
          openHistoryWindow={openHistoryWindow}
          openRightsWindow={openRightsWindow}
          deleteUser={deleteUser}
          resendActivation={resendActivation}
          goBack={goBack}
          getPicture={getPicture}
          handlePreferences={handlePreferences}
        />
      )}
    </React.Fragment>
  );
};
